/**
 * Google Analytics 4 for the public site.
 *
 * The tag loads only when the page is served from exactly readtheword.app, so
 * staging, the droplet host, and localhost send nothing. The production build
 * has no web env file, so the measurement ID defaults to the readtheword.app
 * property; VITE_GA_MEASUREMENT_ID can override it.
 *
 * Only page views are sent, and never user ids or account data. Query values
 * that can carry secrets or personal data (email-verification and
 * password-reset tokens, emails) are redacted before they leave the browser.
 */

export const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID || 'G-EX86QFREZ1';

export const ANALYTICS_HOSTNAME = 'readtheword.app';

const SENSITIVE_PARAMS = ['token', 'code', 'email', 'password', 'key', 'invite'];

let initialized = false;
let lastPage = null;

export function isAnalyticsHost(hostname) {
  return hostname === ANALYTICS_HOSTNAME;
}

export function sanitizeSearch(search) {
  if (!search) return '';
  const params = new URLSearchParams(search);
  for (const name of [...params.keys()]) {
    if (SENSITIVE_PARAMS.includes(name.toLowerCase())) {
      params.set(name, 'redacted');
    }
  }
  const cleaned = params.toString();
  return cleaned ? `?${cleaned}` : '';
}

function pageLocation(origin, pathname, search) {
  return `${origin}${pathname}${sanitizeSearch(search)}`;
}

/**
 * Loads gtag.js and configures the property. Returns false (and touches
 * nothing) on any host other than readtheword.app.
 */
export function initAnalytics({ win = window, doc = document } = {}) {
  if (!isAnalyticsHost(win.location.hostname)) return false;
  if (initialized) return true;
  initialized = true;

  win.dataLayer = win.dataLayer || [];
  // gtag.js only reads Arguments objects from the dataLayer; a rest-args
  // array is silently ignored.
  win.gtag = function gtag() {
    win.dataLayer.push(arguments);
  };
  win.gtag('js', new Date());
  win.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: false,
    page_location: pageLocation(win.location.origin, win.location.pathname, win.location.search),
  });

  const script = doc.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(GA_MEASUREMENT_ID)}`;
  doc.head.appendChild(script);
  return true;
}

/** Sends one page_view for a client-side route. No-op off readtheword.app. */
export function trackPageView(pathname, search, { win = window, doc = document } = {}) {
  if (!initAnalytics({ win, doc })) return false;

  const page = `${pathname}${sanitizeSearch(search)}`;
  // The router remounts when auth finishes loading; don't count that twice.
  if (page === lastPage) return false;
  lastPage = page;

  win.gtag('event', 'page_view', {
    page_path: page,
    page_location: `${win.location.origin}${page}`,
    page_title: doc.title,
  });
  return true;
}

// Test hook: forget the module state between cases.
export function resetAnalyticsForTests() {
  initialized = false;
  lastPage = null;
}
